(() => {
  const initialize = scope => {
    scope.querySelectorAll('[data-wvp-carousel]').forEach(carousel => {
      if (carousel.dataset.ready) return;
      const track = carousel.querySelector('[data-wvp-track]');
      const cards = [...track.children];
      const controls = carousel.querySelector('[data-wvp-controls]');
      const position = carousel.querySelector('[data-wvp-position]');
      const playback = carousel.querySelector('[data-wvp-playback]');
      const progress = carousel.querySelector('[data-wvp-progress]');
      const duration = 5000;
      if (cards.length < 2) return;
      const events = new AbortController();
      const listen = (target, type, callback) => target.addEventListener(type, callback, {signal:events.signal});
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const compact = matchMedia('(max-width: 749px)');
      const heading = carousel.querySelector('.wvp__heading');
      const placeControls = () => {
        const parent = compact.matches ? carousel : heading;
        if (controls.parentElement === parent) return;
        const focused = controls.contains(document.activeElement) ? document.activeElement : null;
        parent.append(controls);
        focused?.focus({preventScroll:true});
      };
      placeControls();
      listen(compact, 'change', placeControls);
      let paused = motion.matches;
      let hovered = false;
      let visible = false;
      let stops = [];
      let active = 0;
      let timer = null;
      let remaining = duration;
      let startedAt = 0;
      let frame;
      let progressFrame;
      carousel.dataset.ready = 'true';
      const running = () => !paused && !hovered && visible && !document.hidden && stops.length > 1 && carousel.isConnected;
      const renderProgress = () => {
        if (!progress) return;
        const elapsed = timer === null ? 0 : performance.now() - startedAt;
        const fraction = Math.min(1, Math.max(0, (duration - remaining + elapsed) / duration));
        progress.style.transform = `scaleX(${fraction})`;
      };
      const animateProgress = () => {
        renderProgress();
        if (timer !== null && running()) progressFrame = requestAnimationFrame(animateProgress);
      };
      const stopTimer = () => {
        if (timer === null) return;
        clearTimeout(timer);
        timer = null;
        remaining = Math.max(0, remaining - (performance.now() - startedAt));
        cancelAnimationFrame(progressFrame);
        renderProgress();
      };
      const reconcile = () => {
        playback.dataset.paused = String(paused);
        const label = paused ? playback.dataset.play : playback.dataset.pause;
        playback.setAttribute('aria-label', label);
        playback.title = label;
        position.setAttribute('aria-live', running() ? 'off' : 'polite');
        if (running() && timer === null) {
          startedAt = performance.now();
          timer = setTimeout(() => {
            timer = null;
            remaining = duration;
            if (running()) go(active + 1);
          }, remaining);
          cancelAnimationFrame(progressFrame);
          animateProgress();
        } else if (!running()) stopTimer();
      };
      const update = () => {
        position.textContent = `${active + 1} / ${stops.length}`;
      };
      const reset = () => {
        clearTimeout(timer);
        timer = null;
        remaining = duration;
        cancelAnimationFrame(progressFrame);
        renderProgress();
      };
      const go = (index, manual = false) => {
        if (!stops.length) return;
        if (manual) paused = true;
        const wrapped = index < 0 || index >= stops.length;
        active = (index + stops.length) % stops.length;
        track.scrollTo({left:stops[active],behavior:motion.matches || wrapped ? 'instant' : 'smooth'});
        update();
        reset();
        reconcile();
      };
      const measure = () => {
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        const start = cards[0].getBoundingClientRect().left;
        const candidates = cards.map(card => Math.min(maximum, Math.max(0, card.getBoundingClientRect().left - start)));
        stops = candidates.filter((offset, index) => !index || Math.abs(offset - candidates[index - 1]) > 1);
        active = Math.min(active, stops.length - 1);
        controls.hidden = stops.length < 2;
        track.scrollTo({left:stops[active],behavior:'instant'});
        update();
        reset();
        reconcile();
      };
      listen(track, 'scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const nearest = stops.reduce((candidate, offset, index) => Math.abs(track.scrollLeft - offset) < Math.abs(track.scrollLeft - stops[candidate]) ? index : candidate, 0);
          active = nearest;
          update();
        });
      });
      listen(carousel.querySelector('[data-wvp-previous]'), 'click', () => go(active - 1, true));
      listen(carousel.querySelector('[data-wvp-next]'), 'click', () => go(active + 1, true));
      listen(playback, 'click', () => { paused = !paused; reconcile(); });
      const pause = () => { paused = true; reset(); reconcile(); };
      listen(track, 'pointerdown', pause);
      listen(track, 'wheel', event => { if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) pause(); });
      listen(track, 'focusin', pause);
      listen(carousel, 'focusin', event => { if (event.target !== playback && event.target.matches(':focus-visible')) pause(); });
      listen(track, 'pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; reconcile(); } });
      listen(track, 'pointerleave', () => { hovered = false; reconcile(); });
      listen(document, 'visibilitychange', reconcile);
      listen(motion, 'change', () => { if (motion.matches) paused = true; reconcile(); });
      const intersection = new IntersectionObserver(entries => { visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .25; reconcile(); }, {threshold:[0,.25]});
      const resize = new ResizeObserver(measure);
      intersection.observe(carousel);
      resize.observe(track);
      measure();
      listen(document, 'shopify:section:unload', event => {
        if (!event.target.contains(carousel)) return;
        stopTimer();
        cancelAnimationFrame(frame);
        cancelAnimationFrame(progressFrame);
        intersection.disconnect();
        resize.disconnect();
        events.abort();
        delete carousel.dataset.ready;
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document));
  else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
